import {IEventEmitter, TypesCore} from "@dp-instant/interfaces";
import {Localize} from "@dp-instant/i18n";
import {AdResponse} from "@dp-instant/interfaces";
import {UIEvents, useInjection, visibilityEffect} from "@dp-instant/ui";
import {SIOConstants} from "@game/constants";
import {getMultiplierStarsPrice} from "../../../packages/shared/src/pricing";
import {GameModel} from "@game/coregame/model/GameModel";
import {showAd, showRewardOrPay} from "@game/helpers";
import {TypesGame} from "@game/TypesGame";
import {CoinsIndicator} from "@game/ui/components/CoinsIndicator/CoinsIndicator";
import {PopupWinIndicator} from "@game/ui/components/CoinsIndicator/PopupWinIndicator";
import {ContinueButton} from "@game/ui/components/ContinueButton/ContinueButton";
import {NoThanksButton} from "@game/ui/components/NoThanksButton/NoThanksButton";
import {WinRays} from "@game/ui/components/WinRays/WinRays";
import {WinStars} from "@game/ui/components/WinStars/WinStars";
import {UIConstants} from "@game/ui/constants";
import {IWinPopupProps} from "@game/ui/interface/IWinPopupProps";
import cn from "classnames";
import {useEffect, useState} from "preact/hooks";
import {ClaimButton} from "../components/ClaimButton/ClaimButton";
import {SVG} from "../images";
import "./WinStagePopup.less";
import {WaitAction} from "@dp-instant/core";
import {apiClient} from "@game/api/ApiClient";

let shownInterstialAfterLevelCounter = 0;

interface WinStagePopupProps extends IWinPopupProps {}
export const WinStagePopup = ({
    animationEnabled = true,
    coins = 9999,
    showRewardAd = true,
    onContinue
}: WinStagePopupProps) => {
    const model = useInjection<GameModel>(TypesGame.model);
    const dispatcher = useInjection<IEventEmitter>(TypesCore.dispatcher);
    const [invisible, setInvisible] = visibilityEffect(UIConstants.popup.startDelay);
    if (__SOCIAL__ === "tg") apiClient.trackFunnelEvent("gift_screen_shown");

    const [state, setState] = useState({
        startCoinsTotal: model.cookie.coins,
        adViewed: false,
        reward: 0
    });
    const maxReward = coins * SIOConstants.CLAIM_FACTOR;

    useEffect(() => {
        if (state.adViewed) {
            setState({...state, reward: maxReward});
        } else {
            const tickTimeout = setTimeout(() => setState({...state, reward: coins}), 200);
            return () => clearTimeout(tickTimeout);
        }
    }, [state.adViewed]);

    const close = async (success = false) => {
        setInvisible(true);
        model.cookie.coins += state.reward;
        await WaitAction.ms(UIConstants.coinsIndicator.updateDelay);
        dispatcher.emit(UIEvents.POPUP, {id: null});
        onContinue && (await onContinue(success));
    };
    const onClaim = async () => {
        const results = await showRewardOrPay(model.cookie.getVip, "reward_x3");
        if (results) {
            setState(state => ({...state, adViewed: true}));
            if (__SOCIAL__ === "tg") apiClient.trackFunnelEvent("gift_claimed", { type: "reward_x3" });
        }
    };

    /**
     * @description Limits ad showing after level end for Yandex. It shows ad once per two levels.
     * @returns boolean
     */
    const canShowNextInter = () => {
        return __SOCIAL__ === "ya" ? !!(shownInterstialAfterLevelCounter % 2) : true;
    };

    const onDecline = async () => {
        if (__SOCIAL__ !== "tg" && canShowNextInter()) {
            await showAd();
        }
        shownInterstialAfterLevelCounter++;
        await close(false);
    };
    const showClaim = showRewardAd && !state.adViewed;

    return (
        <div className="popups">
            <div className={cn("coins-bar")}>
                <CoinsIndicator
                    className={cn("coins-indicator", "coins-indicator_filled")}
                    total={state.startCoinsTotal}
                />
            </div>

            <div className="popup-win-level">
                <div className={cn("popup", {invisible})}>
                    <div className="popup__title">
                        {Localize.get("ui-win-win_label", "YOU WON")}
                    </div>
                    {animationEnabled ? (
                        <div className="popup__main-animation">
                            <SVG.PopupWinCup />
                            <WinRays />
                            <WinStars />
                        </div>
                    ) : null}
                    <div className="popup__body">
                        <div className="popup__sub-title">
                            {Localize.get("ui-win-reward_label", "YOUR REWARD")}
                        </div>
                        <PopupWinIndicator
                            className={cn("coins-indicator", "coins-indicator_internal")}
                            total={state.reward}
                        />
                    </div>
                    {showClaim ? (
                        <div className={"popup__body-buttons"}>
                            <div className={cn("popup__body-button", "center")}>
                                {!state.adViewed ? (
                                    <ClaimButton onClick={onClaim} reward={maxReward} starsPrice={getMultiplierStarsPrice(coins)} />
                                ) : null}
                            </div>
                        </div>
                    ) : null}

                    {showClaim ? (
                        <NoThanksButton
                            delay={UIConstants.popup.noThanksButtonDelay}
                            onClick={onDecline}
                        />
                    ) : (
                        <ContinueButton onClick={() => close(true)} />
                    )}
                </div>
            </div>
        </div>
    );
};
